/**
 * Statistiques de révision (spécification §9, contenu proposé par le design system), tirées
 * du journal des révisions et de l'état des cartes. Les jours sont ceux du paquet (heure de
 * début de journée).
 */
import { State, type Note } from '../fsrs/planificateur';
import type { ReglagesPaquet } from '../paquets/reglages';
import type { CarteDuPaquet, RevisionPassee } from './composition';
import { debutJournee } from './journee';

export interface RevisionDuJournal extends RevisionPassee {
  note: Note;
  tempsReponseMs: number;
}

/** Journée du paquet au format AAAA-MM-JJ. */
export function cleDuJour(instant: Date, heureDebut: number): string {
  const d = debutJournee(instant, heureDebut);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function decaler(jour: Date, n: number): Date {
  const d = new Date(jour);
  d.setDate(d.getDate() + n);
  return d;
}

export interface ActiviteDuJour {
  date: string;
  nouvelles: number;
  apprentissage: number;
  acquises: number;
  secondes: number;
}

/**
 * Cartes revues chaque jour des `n` derniers jours (aujourd'hui en dernier), une fois par
 * jour, dans leur état à leur première révision du jour ; temps de réponse cumulé.
 */
export function activite(
  revisions: readonly RevisionDuJournal[],
  heureDebut: number,
  maintenant: Date,
  n = 30,
): ActiviteDuJour[] {
  const aujourdhui = debutJournee(maintenant, heureDebut);
  const jours = new Map<string, ActiviteDuJour & { vues: Set<string> }>();
  for (let i = n - 1; i >= 0; i--) {
    const date = cleDuJour(decaler(aujourdhui, -i), heureDebut);
    jours.set(date, {
      date,
      nouvelles: 0,
      apprentissage: 0,
      acquises: 0,
      secondes: 0,
      vues: new Set(),
    });
  }
  const triees = [...revisions].sort((a, b) => a.reviseLe.getTime() - b.reviseLe.getTime());
  for (const r of triees) {
    const jour = jours.get(cleDuJour(r.reviseLe, heureDebut));
    if (!jour) continue;
    jour.secondes += r.tempsReponseMs / 1000;
    if (jour.vues.has(r.carteId)) continue;
    jour.vues.add(r.carteId);
    if (r.etatAvant === State.New) jour.nouvelles += 1;
    else if (r.etatAvant === State.Review) jour.acquises += 1;
    else jour.apprentissage += 1;
  }
  return [...jours.values()].map(({ vues: _vues, ...jour }) => ({
    ...jour,
    secondes: Math.round(jour.secondes),
  }));
}

/** Lundi de la semaine d'une journée AAAA-MM-JJ, au même format. */
export function lundiDe(cle: string): string {
  const [a, m, j] = cle.split('-').map(Number);
  const jour = new Date(a!, m! - 1, j!);
  const lundi = decaler(jour, -((jour.getDay() + 6) % 7));
  return `${lundi.getFullYear()}-${String(lundi.getMonth() + 1).padStart(2, '0')}-${String(lundi.getDate()).padStart(2, '0')}`;
}

/**
 * Activité regroupée par semaine (du lundi), les `n` dernières semaines, la semaine en cours
 * en dernier. `date` est le lundi ; une carte compte une fois par jour de révision.
 */
export function activiteParSemaine(
  revisions: readonly RevisionDuJournal[],
  heureDebut: number,
  maintenant: Date,
  n = 52,
): ActiviteDuJour[] {
  const semaines = new Map<string, ActiviteDuJour>();
  for (const jour of activite(revisions, heureDebut, maintenant, 7 * n + 6)) {
    const date = lundiDe(jour.date);
    const semaine = semaines.get(date);
    if (!semaine) semaines.set(date, { ...jour, date });
    else {
      semaine.nouvelles += jour.nouvelles;
      semaine.apprentissage += jour.apprentissage;
      semaine.acquises += jour.acquises;
      semaine.secondes += jour.secondes;
    }
  }
  return [...semaines.values()].slice(-n);
}

export interface PrevisionDuJour {
  date: string;
  nouvelles: number;
  apprentissage: number;
  acquises: number;
}

/**
 * Cartes dues chacun des `n` prochains jours (aujourd'hui en premier) : acquises à leur
 * échéance (aujourd'hui, retards compris), apprentissage à leur étape suivante, nouvelles
 * selon la limite quotidienne (aucune en ordre par probabilité de rappel). Les limites de
 * révisions ne sont pas appliquées : c'est la charge prévue.
 */
export function previsions(
  cartes: readonly CarteDuPaquet[],
  reglages: Pick<ReglagesPaquet, 'heureDebutJournee' | 'nouvellesCartesParJour' | 'ordreCartes'>,
  maintenant: Date,
  nouvellesDuJourRestantes: number,
  n = 30,
): PrevisionDuJour[] {
  const heure = reglages.heureDebutJournee;
  const aujourdhui = debutJournee(maintenant, heure);
  const jours = Array.from({ length: n }, (_, i): PrevisionDuJour => ({
    date: cleDuJour(decaler(aujourdhui, i), heure),
    nouvelles: 0,
    apprentissage: 0,
    acquises: 0,
  }));
  const index = new Map(jours.map((j, i) => [j.date, i]));
  let nouvelles = 0;
  for (const carte of cartes) {
    if (carte.suspendue) continue;
    if (carte.fsrs.state === State.New) {
      nouvelles += 1;
      continue;
    }
    const due = carte.fsrs.due.getTime() < aujourdhui.getTime() ? aujourdhui : carte.fsrs.due;
    const i = index.get(cleDuJour(due, heure));
    if (i === undefined) continue;
    if (carte.fsrs.state === State.Review) jours[i]!.acquises += 1;
    else jours[i]!.apprentissage += 1;
  }
  if (reglages.ordreCartes !== 'probabilite_rappel') {
    let restantes = nouvelles;
    jours.forEach((jour, i) => {
      const limite = i === 0 ? nouvellesDuJourRestantes : reglages.nouvellesCartesParJour;
      jour.nouvelles = Math.max(0, Math.min(limite, restantes));
      restantes -= jour.nouvelles;
    });
  }
  return jours;
}

export interface RetentionDeLaSemaine {
  /** Lundi de la semaine, AAAA-MM-JJ. */
  debut: string;
  revisions: number;
  aRevoir: number;
}

/** Réponses données à des cartes acquises, par semaine (lundi), les `n` dernières semaines. */
export function retention(
  revisions: readonly RevisionDuJournal[],
  heureDebut: number,
  maintenant: Date,
  n = 12,
): RetentionDeLaSemaine[] {
  const aujourdhui = debutJournee(maintenant, heureDebut);
  const lundi = decaler(aujourdhui, -((aujourdhui.getDay() + 6) % 7));
  const semaines = Array.from({ length: n }, (_, i): RetentionDeLaSemaine => ({
    debut: cleDuJour(decaler(lundi, -7 * (n - 1 - i)), heureDebut),
    revisions: 0,
    aRevoir: 0,
  }));
  const index = new Map(semaines.map((s, i) => [s.debut, i]));
  for (const r of revisions) {
    if (r.etatAvant !== State.Review) continue;
    const jour = debutJournee(r.reviseLe, heureDebut);
    const debut = cleDuJour(decaler(jour, -((jour.getDay() + 6) % 7)), heureDebut);
    const i = index.get(debut);
    if (i === undefined) continue;
    semaines[i]!.revisions += 1;
    if (r.note === 1) semaines[i]!.aRevoir += 1;
  }
  return semaines;
}

export interface ChiffresCles {
  cartesRevisees: number;
  /** Taux de réussite sur les cartes acquises (0 à 1), ou `null` sans révision de carte acquise. */
  tauxDeReussite: number | null;
  /** En secondes. */
  tempsPasse: number;
  /** Variation par rapport à la période précédente, en fraction (0,12 = +12 %), ou `null`. */
  variationCartes: number | null;
  variationTemps: number | null;
}

/** Journal d'un paquet principal et son heure de début de journée. */
export interface JournalDuPaquet {
  revisions: readonly RevisionDuJournal[];
  heureDebut: number;
}

/**
 * Chiffres clés de la période (les `jours` derniers jours, aujourd'hui compris) et variation
 * sur la période précédente de même durée. Une carte révisée plusieurs fois le même jour compte
 * une fois ce jour-là.
 */
export function chiffresCles(
  journaux: readonly JournalDuPaquet[],
  maintenant: Date,
  jours: number,
): ChiffresCles {
  const mesurer = (decalage: number) => {
    let cartes = 0;
    let acquises = 0;
    let reussies = 0;
    let temps = 0;
    for (const { revisions, heureDebut } of journaux) {
      const fin = decaler(debutJournee(maintenant, heureDebut), 1 - decalage * jours);
      const debut = decaler(fin, -jours);
      const vues = new Set<string>();
      for (const r of revisions) {
        const t = r.reviseLe.getTime();
        if (t < debut.getTime() || t >= fin.getTime()) continue;
        vues.add(`${r.carteId}|${cleDuJour(r.reviseLe, heureDebut)}`);
        temps += r.tempsReponseMs / 1000;
        if (r.etatAvant === State.Review) {
          acquises += 1;
          if (r.note !== 1) reussies += 1;
        }
      }
      cartes += vues.size;
    }
    return { cartes, taux: acquises === 0 ? null : reussies / acquises, temps };
  };
  const actuelle = mesurer(0);
  const precedente = mesurer(1);
  const variation = (a: number, b: number) => (b === 0 ? null : (a - b) / b);
  return {
    cartesRevisees: actuelle.cartes,
    tauxDeReussite: actuelle.taux,
    tempsPasse: Math.round(actuelle.temps),
    variationCartes: variation(actuelle.cartes, precedente.cartes),
    variationTemps: variation(actuelle.temps, precedente.temps),
  };
}

/**
 * Additionne, entrée par entrée, les séries de même longueur de plusieurs paquets principaux
 * (« Tous les paquets ») ; les champs non numériques sont ceux de la première série.
 */
export function additionner<T extends object>(series: readonly (readonly T[])[]): T[] {
  const [premiere, ...autres] = series;
  if (!premiere) return [];
  return premiere.map((entree, i) => {
    const somme: Record<string, unknown> = Object.fromEntries(Object.entries(entree));
    for (const serie of autres) {
      const autre: object | undefined = serie[i];
      if (!autre) continue;
      for (const [cle, valeur] of Object.entries(autre)) {
        if (typeof valeur === 'number') somme[cle] = (somme[cle] as number) + valeur;
      }
    }
    return somme as T;
  });
}
